import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormTypesData, KeyValueData } from '../../../domains/common.domain';
import { TechnicianDataService } from '../technician-data.service';
import { Subscription } from 'rxjs';
import { LocalStorageService } from '../../../services/local-storage.service';
import { AllLabels, InstallationFlowType } from '../../../constants/app.constants';
import { AllDropdownKeys, UrlSegments } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { CheckServiceAvailabilityData, CheckServiceAvailabilityResponse, LookupOptionsData } from '../../jurisdiction.domain';
import { CommunicationService } from '../../../services/communication.service';
import { ErrorService } from '../../../services/error/error.service';
import { JurisdictionService } from '../../jurisdiction.service';
import { EnvironmentService } from '../../../services/environment.service';
import { ConnectionType } from 'src/app/+shared/enums/connection-types.enum';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';

@Component({
  selector: 'app-technician-base-data',
  styleUrls: ['./base-data.component.scss'],
  templateUrl: './base-data.component.html',
})
export class TechnicianBaseDataComponent implements OnChanges, OnInit {
  @Input() currentFormType: FormTypesData;
  @Input() baseData: any = null;
  @Input() isReadOnly: boolean = false;
  @Input() set selectedFacilities(value: string[]) {
    this._selectedFacilities = value;
    this.showConnectionOptions = this.shouldShowConnectionOptions();

    this.updateConnectionsTypeFormControl();
  }
  @Input() applicationId: number;
  @Input() isDetailsView: boolean;

  _selectedFacilities: string[] = [];
  disableSiteWater: boolean;

  formSubscription: Subscription;
  technicianBaseDataFG: FormGroup;
  currentFlowType: string;
  changeConnectionType: string;
  changeConnectionTypes: LookupOptionsData[];
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowType: KeyValueData;
  isLoading: boolean;
  showTrenchLengthSection: boolean;
  isTrenchLengthFixed: boolean;
  isTrenchLengthOver30: boolean;
  isTrenchLengthFieldsRequired: boolean;
  showCommercialUnitCount: boolean;
  showJurisdictionNotice: boolean;
  showOtherInquiriesSection: boolean;
  showPowerSection: boolean;
  showReasonForReinforcement: boolean;
  showResidentialUnitCount: boolean;
  showWaterSection: boolean;
  specialDevices: FormArray;
  specialDeviceTypeKey: string;
  shutdownReasonKey: string;
  shutdownReasonTypes: LookupOptionsData[];
  specialDeviceTypes: LookupOptionsData[];
  allUnits: {
    [key: string]: string;
  };
  unitLabels: KeyValueData;

  showGenericBaseDataSection: boolean;
  isDecommissioningOfGridConnection: boolean;
  showHALengthsSection: boolean;
  isNewDevArea: boolean;
  showConnectionOptions: boolean;

  connectionTypes = { ...ConnectionType };
  showTempConnectionsInfo = false;
  sectionIdentifier = SectionIdentifier;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    public environmentService: EnvironmentService,
    private errorService: ErrorService,
    private technicianDataService: TechnicianDataService,
    private localStorageService: LocalStorageService,
    private jurisdictionService: JurisdictionService
  ) {}

  ngOnInit(): void {
    this.installationFlowType = InstallationFlowType;
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.changeConnectionType = this.localStorageService.getItem('selected_change_type');
    this.changeConnectionTypes = this.commonService.getDropdownData(AllDropdownKeys.ChangeSubtype);
    this.allUnits = AllLabels.units;
    this.specialDeviceTypeKey = AllDropdownKeys.TempSpecialConsumerType;
    this.specialDeviceTypes = this.commonService.getDropdownData(this.specialDeviceTypeKey);
    this.shutdownReasonKey = AllDropdownKeys.SwknShutdownReason;
    this.shutdownReasonTypes = this.commonService.getDropdownData(this.shutdownReasonKey);
    this.showCommercialUnitCount = false;
    this.showReasonForReinforcement = this.environmentService.showReasonForReinforcement;
    this.showResidentialUnitCount = false;
    this.unitLabels = AllLabels.units;
    this.showPowerSection = false;
    this.showWaterSection = false;
    this.showJurisdictionNotice = false;
    this.isLoading = false;
    this.showTrenchLengthSection = this.environmentService.showTrenchLengthSection;
    this.isTrenchLengthFixed = this.environmentService.trenchLengthFixed;
    this.isTrenchLengthOver30 = this.environmentService.trenchLengthOver30;
    this.isTrenchLengthFieldsRequired = this.environmentService.trenchLengthFieldsRequired;
    this.showOtherInquiriesSection = this.environmentService.otherInquiriesSection;
    this.showHALengthsSection =
      this.environmentService.showHALengthsSection && this.currentFlowType === this.installationFlowType.MainsConnection;
    this.showTempConnectionsInfo =
      this.environmentService.showTempConnectionsInfo && this.currentFlowType === this.installationFlowType.MainsConnection;
    this.showGenericBaseDataSection = this.environmentService.showGenericBaseDataSection || !this.showHALengthsSection;
    this.showConnectionOptions = this.shouldShowConnectionOptions();
    this.isDecommissioningOfGridConnection = this.environmentService.isDecommissioningOfGridConnection;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('technicianData') || null;
    this.jurisdictionService.fetchNewDevAreaMessage().subscribe(newDevArea => {
      this.isNewDevArea = newDevArea;
    });
    this.prepareForm();
    this.technicianDataService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.emitFormData();
        this.resetData();
      }
    });
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes && changes.currentFormType && !changes.currentFormType.firstChange) ||
      (changes && changes.baseData && !changes.baseData.firstChange)
    ) {
      this.prepareForm();
    }
  }

  private prepareForm(): void {
    const selectedMediaTypes = this.localStorageService.getItem('selected_facilities') || [];
    const isWaterFacilityAvailable =
      !this.environmentService.hideCommodityWater && selectedMediaTypes.some(facility => facility.key === 'water');
    const isPowerFacilityAvailable = selectedMediaTypes.some(facility => facility.key === 'power');
    const isGasOrHeatingFacilityAvailable = selectedMediaTypes.some(facility => facility.key === 'gas' || facility.key === 'heating');
    this.disableSiteWater = isWaterFacilityAvailable;
    this.technicianBaseDataFG = new FormGroup({});
    if (this.isDecommissioningOfGridConnection && this.changeConnectionType === this.changeConnectionTypes[0].value) {
      this.technicianBaseDataFG.addControl('shutDownReason', new FormControl(this.baseData?.shutDownReason || ''));
      if (this.baseData?.shutDownReason === 'other') {
        this.technicianBaseDataFG.addControl('otherShutDownReason', new FormControl(this.baseData?.otherShutDownReason || ''));
      }
      this.technicianBaseDataFG.get('shutDownReason').valueChanges.subscribe(reason => {
        if (reason === 'other') {
          this.technicianBaseDataFG.addControl('otherShutDownReason', new FormControl(this.baseData?.otherShutDownReason || ''));
        } else {
          this.technicianBaseDataFG.removeControl('otherShutDownReason');
        }
      });
    }
    if (this.environmentService.showSiteElectricityCheckbox && this.isNewDevArea && isPowerFacilityAvailable) {
      this.technicianBaseDataFG.addControl('siteElectricityCheckbox', new FormControl(this.baseData?.siteElectricityCheckbox || false));
    }

    if (!this.environmentService.showSiteFacilitiesCheckboxExclusively || (isWaterFacilityAvailable && this.isNewDevArea)) {
      this.technicianBaseDataFG.addControl('siteWaterCheckbox', new FormControl(this.baseData?.siteWaterCheckbox || false));
    }

    if (this.currentFlowType === this.installationFlowType.MainsConnection) {
      if (this.environmentService.hideSiteWaterCheckbox) {
        this.technicianBaseDataFG.removeControl('siteWaterCheckbox');
      }

      if (isPowerFacilityAvailable) {
        if (this.environmentService.showFeedInSystemCheckbox) {
          this.technicianBaseDataFG.addControl('feedInSystemCheckbox', new FormControl(this.baseData?.feedInSystemCheckbox || false));
        }
        if (this.environmentService.showWallboxCheckbox) {
          this.technicianBaseDataFG.addControl('wallboxCheckbox', new FormControl(this.baseData?.wallboxCheckbox || false));
        }
        if (this.environmentService.showPlannedOutputCheckbox) {
          this.technicianBaseDataFG.addControl('plannedOutputCheckbox', new FormControl(this.baseData?.plannedOutputCheckbox || false));
        }
      }
      if (this.environmentService.showInterestInFibreCheckbox) {
        this.technicianBaseDataFG.addControl('interestInFibreCheckbox', new FormControl(this.baseData?.interestInFibreCheckbox || false));
      }
      if (this.environmentService.showGeneratingPlantCheckbox && isGasOrHeatingFacilityAvailable) {
        this.technicianBaseDataFG.addControl('generatingPlantCheckbox', new FormControl(this.baseData?.generatingPlantCheckbox || false));
      }
      if (this.environmentService.showEarthworkContributionCheckbox) {
        this.technicianBaseDataFG.addControl(
          'earthworkContributionCheckbox',
          new FormControl(this.baseData?.earthworkContributionCheckbox || false)
        );
      }
      if (this.environmentService.showTelecomConnectionCheckbox) {
        this.technicianBaseDataFG.addControl(
          'telecomConnectionCheckbox',
          new FormControl(this.baseData?.telecomConnectionCheckbox || false)
        );
      }
      if (this.environmentService.showWidebandConnectionCheckbox) {
        this.technicianBaseDataFG.addControl(
          'widebandConnectionCheckbox',
          new FormControl(this.baseData?.widebandConnectionCheckbox || false)
        );
      }
      if (this.technicianBaseDataFG.get('siteWaterCheckbox') && this.baseData.siteWaterCheckbox) {
        this.checkJurisdictionAvailability(true);
      }

      if (this.showHALengthsSection) {
        this.technicianBaseDataFG.addControl('haLengths', new FormControl(this.baseData?.haLengths || '', [Validators.required]));
        this.technicianBaseDataFG.addControl('basementAvailable', new FormControl(this.baseData?.basementAvailable || false));
        this.technicianBaseDataFG.addControl(
          'houseConnectionRoomAvailable',
          new FormControl(this.baseData?.houseConnectionRoomAvailable || false)
        );
      }
    }

    if (this.currentFormType.index !== 0 && this.currentFormType.index !== 4) {
      this.technicianBaseDataFG.addControl('residentialUnitsCount', new FormControl(this.baseData?.residentialUnitsCount || ''));
      this.technicianBaseDataFG.addControl('commercialUnitsCount', new FormControl(this.baseData?.commercialUnitsCount || ''));
      this.showCommercialUnitCount = this.baseData?.business;
      this.showResidentialUnitCount = this.baseData?.residentialUnits;
    }

    const role = this.localStorageService.getItem('role');
    if (
      (role.toLowerCase() !== 'customer' || (role.toLowerCase() === 'customer' && this.environmentService.showPublicFacilityForCustomer)) &&
      this.currentFormType.index > 0 &&
      this.currentFlowType === this.installationFlowType.MainsConnection
    ) {
      this.technicianBaseDataFG.addControl(
        'publicationInstitutionCheckbox',
        new FormControl(this.baseData?.publicationInstitutionCheckbox || false)
      );
    }

    const validator = this.showField() ? Validators.required : null;
    if (this.currentFormType.index < 2 || this.showTrenchLengthSection) {
      if (this.isTrenchLengthOver30) {
        this.technicianBaseDataFG.addControl('connectionLineCheckbox', new FormControl(this.baseData?.connectionLineCheckbox || false));
      }
      if (this.isTrenchLengthFixed) {
        this.technicianBaseDataFG.addControl(
          'cableLengthTotal',
          new FormControl(this.baseData?.cableLengthTotal || '', this.isTrenchLengthFieldsRequired ? null : validator)
        );
        this.technicianBaseDataFG.addControl(
          'cableLengthFixed',
          new FormControl(this.baseData?.cableLengthFixed || '', this.isTrenchLengthFieldsRequired ? null : validator)
        );
      }
    }
    if (!this.environmentService.showCableLengthFixed) {
      this.technicianBaseDataFG.removeControl('cableLengthFixed');
    }
    if (this.showOtherInquiriesSection) {
      if (this.showField()) {
        this.technicianBaseDataFG.addControl(
          'swsConLeadBasement',
          new FormControl(
            {
              value: this.baseData?.swsConLeadBasement || '',
              disabled: this.isReadOnly,
            },
            [Validators.required]
          )
        );
        this.technicianBaseDataFG.addControl(
          'swsWaterProofBasement',
          new FormControl(
            {
              value: this.baseData?.swsWaterProofBasement || '',
              disabled: this.isReadOnly,
            },
            [Validators.required]
          )
        );
        this.technicianBaseDataFG.addControl(
          'swsConLeadMainBuilding',
          new FormControl(
            {
              value: this.baseData?.swsConLeadMainBuilding || '',
              disabled: this.isReadOnly,
            },
            [Validators.required]
          )
        );
      }
      this.technicianBaseDataFG.addControl(
        'swsWithBasement',
        new FormControl(
          {
            value: this.baseData?.swsWithBasement || '',
            disabled: this.isReadOnly,
          },
          [Validators.required]
        )
      );
      this.technicianBaseDataFG.addControl(
        'swsThirdPartyAffected',
        new FormControl(
          {
            value: this.baseData?.swsThirdPartyAffected || '',
            disabled: this.isReadOnly,
          },
          [Validators.required]
        )
      );
      if (this.environmentService.showSwsPlotSize) {
        if (
          !(
            !this.environmentService.showSwsPlotSizeForMainsConnection && this.currentFlowType === this.installationFlowType.MainsConnection
          )
        ) {
          this.technicianBaseDataFG.addControl('swsPlotSize', new FormControl(this.baseData?.swsPlotSize || '', validator));
        }
      }
    }

    if (
      this.currentFlowType === this.installationFlowType.ConversionToGas ||
      this.currentFlowType === this.installationFlowType.ChangeHouseConnection
    ) {
      if (
        role.toLowerCase() !== 'customer' ||
        (role.toLowerCase() === 'customer' && this.environmentService.showResidentialUnitsForCustomer)
      ) {
        this.technicianBaseDataFG.addControl('residentialUnits', new FormControl(this.baseData?.residentialUnits || false));
      }
      if (role.toLowerCase() !== 'customer' || (role.toLowerCase() === 'customer' && this.environmentService.showBusinessForCustomer)) {
        this.technicianBaseDataFG.addControl('business', new FormControl(this.baseData?.business || false));
      }
    }

    if (this.currentFlowType === this.installationFlowType.ChangeHouseConnection) {
      this.technicianBaseDataFG.addControl('specialDevice', new FormArray([]));

      if (this.baseData.specialDevices?.length) {
        this.baseData.specialDevices.forEach(item => {
          this.addDevice(item);
        });
      } else {
        this.specialDevices = this.technicianBaseDataFG.get('specialDevice') as FormArray;
      }

      if (this.changeConnectionType === this.changeConnectionTypes[0].value) {
        this.technicianBaseDataFG.addControl('totalOutput', new FormControl(this.baseData?.totalOutput || ''));
        this.technicianBaseDataFG.addControl('tax_id', new FormControl(this.baseData?.tax_id || ''));
        this.technicianBaseDataFG.addControl('company_name', new FormControl(this.baseData?.company_name || ''));
        this.technicianBaseDataFG.addControl('telephone', new FormControl(this.baseData?.telephone || ''));
        this.technicianBaseDataFG.addControl('peakFlow', new FormControl(this.baseData?.peakFlow || ''));
        this.showPowerSection = this.baseData?.siteElectricityCheckbox || false;
        this.showWaterSection = this.baseData?.siteWaterCheckbox || false;
      } else if (this.showReasonForReinforcement && this.changeConnectionType === this.changeConnectionTypes[1].value) {
        this.technicianBaseDataFG.addControl(
          'reasonForReinforcement',
          new FormControl(this.baseData?.reasonForReinforcement || '', [Validators.required])
        );
      }
    }

    this.updateConnectionsTypeFormControl();

    this.localStorageService.setItem('site_electricity_required', this.baseData?.siteElectricityCheckbox || false);
    this.formSubscription = this.technicianBaseDataFG.valueChanges.subscribe(values => {
      this.localStorageService.setItem('site_electricity_required', values.siteElectricityCheckbox);
      this.showCommercialUnitCount = values.business;
      this.showResidentialUnitCount = values.residentialUnits;
      this.emitFormData();
    });
    this.emitFormData();
  }

  private emitFormData(): void {
    this.technicianDataService.registerForComponentFormUpdate.next({
      component: 'baseData',
      formData: this.technicianBaseDataFG,
    });
  }

  private resetData(): void {
    this.baseData = null;
    this.formSubscription.unsubscribe();
  }

  addDevice(data?: any): void {
    const selectedDevice = (data && data.device && this.specialDeviceTypes.filter(item => item.value === data.device)[0]) || null;

    (this.technicianBaseDataFG.get('specialDevice') as FormArray).push(
      new FormGroup({
        id: new FormControl((data && data.id) || ''),
        device: new FormControl((selectedDevice && selectedDevice.value) || '', [Validators.required]),
        count: new FormControl((data && data.count) || '', [Validators.required]),
        power: new FormControl((data && data.power) || '', [Validators.required]),
      })
    );
    this.specialDevices = this.technicianBaseDataFG.get('specialDevice') as FormArray;
  }

  removeDevice(index: number): void {
    const deviceId = (this.baseData?.specialDevices[index] && this.baseData.specialDevices[index].id) || '';
    if (deviceId) {
      this.communicationService
        .deleteData(`${UrlSegments.UpdatePowerGridSpecialConsumer}${this.baseData.specialDevices[index].id}`)
        .subscribe(
          response => {
            this.errorService.throwErrorIfFailure(response, true);
            (this.technicianBaseDataFG.get('specialDevice') as FormArray).removeAt(index);
          },
          error => {
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
    } else {
      (this.technicianBaseDataFG.get('specialDevice') as FormArray).removeAt(index);
    }
  }

  selectSiteElectricity(event: boolean): void {
    this.showPowerSection = event;
    if (!this.showPowerSection) {
      this.technicianBaseDataFG.get('totalOutput').reset();
      this.specialDevices.controls.forEach((item, index) => {
        this.removeDevice(index);
      });
    }
  }

  selectSiteWater(event: boolean): void {
    this.showWaterSection = event;
    if (!this.showWaterSection) {
      this.technicianBaseDataFG.get('peakFlow').reset();
    }
  }

  checkJurisdictionAvailability(event: any): void {
    const locationData = this.commonService.locationData;
    if (event && locationData) {
      this.isLoading = true;
      const gps = locationData.gps;
      const items = gps.split(',');
      const requestData: CheckServiceAvailabilityData = {
        city: locationData.city,
        district: locationData.district,
        medium: 'water',
        street: locationData.street,
        street_number: locationData.street_number,
        x: items[0] && items[0].trim(),
        y: items[1] && items[1].trim(),
        zipcode: locationData.postcode,
      };
      if (this.currentFlowType === this.installationFlowType.MainsConnection) {
        requestData.development_area = locationData.newDevArea ? '1' : '0';
        requestData.sk_development_area_type = locationData.devAreaName;
      }
      this.jurisdictionService.checkServiceAvailability(requestData).subscribe(
        (response: CheckServiceAvailabilityResponse) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          if (event.checked) {
            this.showJurisdictionNotice =
              (response.regionalstructure && response.regionalstructure.length && response.regionalstructure[0].result) || false;
          } else {
            this.showJurisdictionNotice = false;
          }
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.showJurisdictionNotice = false;
    }
  }

  showField(): boolean {
    return !(
      this.showOtherInquiriesSection &&
      [
        this.installationFlowType.MainsConnection,
        this.installationFlowType.ConversionToGas,
        this.installationFlowType.ChangeHouseConnection,
      ].includes(this.currentFlowType)
    );
  }

  private shouldShowConnectionOptions(): boolean {
    if (!this.environmentService.showConnectionOptions) return false;

    const isHeatingFacilityAvailable = this._selectedFacilities.some(facility => facility === 'heating');
    const isMainsHeating = this.currentFlowType === 'mainsConnection' && isHeatingFacilityAvailable;
    const isChangeOfHouseReinforceHeating =
      this.currentFlowType === 'changeHouseConnection' && this.changeConnectionType === 'reinforce' && isHeatingFacilityAvailable;
    const isConversionToGasOrDistrictHeating =
      this.currentFlowType === 'conversionToGas' && this.changeConnectionType === 'conversion_heat' && isHeatingFacilityAvailable;

    return isMainsHeating || isChangeOfHouseReinforceHeating || isConversionToGasOrDistrictHeating;
  }

  private updateConnectionsTypeFormControl() {
    if (!this.technicianBaseDataFG) return;

    if (this.showConnectionOptions) {
      this.technicianBaseDataFG.addControl('connectionsType', new FormControl(this.baseData.connectionType));
    } else {
      this.technicianBaseDataFG.removeControl('connectionsType');
    }
  }
}
